import React, { useState } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const ActivityCard = ({ activity, onBookNow }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === activity?.images?.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? activity?.images?.length - 1 : prev - 1
    );
  };

  const getDifficultyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'easy': return 'text-success bg-success/10';
      case 'moderate': return 'text-warning bg-warning/10';
      case 'challenging': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="bg-card rounded-lg shadow-organic overflow-hidden hover-lift transition-organic">
      {/* Image Gallery */}
      <div className="relative h-64 overflow-hidden">
        <Image
          src={activity?.images?.[currentImageIndex]}
          alt={`${activity?.name} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover"
        />
        
        {/* Image Navigation */}
        {activity?.images?.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center text-foreground hover:bg-background transition-micro"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center text-foreground hover:bg-background transition-micro"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
            
            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
              {activity?.images?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-micro ${
                    index === currentImageIndex ? 'bg-primary' : 'bg-background/60'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Seasonal Badge */}
        {activity?.seasonal && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-md">
            {activity?.seasonal}
          </div>
        )}

        {/* Availability Badge */}
        <div className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium rounded-md ${
          activity?.available ? 'bg-success text-success-foreground' : 'bg-error text-error-foreground'
        }`}>
          {activity?.available ? 'Available' : 'Fully Booked'}
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-heading font-semibold text-card-foreground mb-1">
              {activity?.name}
            </h3>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={14} />
                <span>{activity?.duration}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={14} />
                <span>{activity?.groupSize}</span>
              </div>
            </div>
          </div>
          <div className={`px-2 py-1 rounded-md text-xs font-medium ${getDifficultyColor(activity?.difficulty)}`}>
            {activity?.difficulty}
          </div>
        </div>

        {/* Description */}
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {activity?.description}
        </p>

        {/* Features */}
        <div className="flex flex-wrap gap-2 mb-4">
          {activity?.features?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-1 text-xs text-muted-foreground">
              <Icon name="Check" size={12} className="text-success" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Safety Requirements */}
        {activity?.safetyRequirements && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-card-foreground mb-2 flex items-center space-x-1">
              <Icon name="Shield" size={14} />
              <span>Safety Requirements</span>
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              {activity?.safetyRequirements?.map((req, index) => (
                <li key={index} className="flex items-start space-x-1">
                  <span className="text-warning mt-0.5">•</span>
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pricing & Booking */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-heading font-bold text-primary">
                {formatCurrency(activity?.price)}
              </span>
              <span className="text-sm text-muted-foreground">per person</span>
            </div>
            {activity?.originalPrice && activity?.originalPrice > activity?.price && (
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm text-muted-foreground line-through">
                  {formatCurrency(activity?.originalPrice)}
                </span>
                <span className="text-xs text-success font-medium">
                  Save {formatCurrency(activity?.originalPrice - activity?.price)}
                </span>
              </div>
            )}
          </div>
          
          <Button
            variant={activity?.available ? "default" : "outline"}
            size="sm"
            disabled={!activity?.available}
            onClick={() => onBookNow(activity)}
            iconName={activity?.available ? "Calendar" : "Clock"}
            iconPosition="left"
          >
            {activity?.available ? "Book Now" : "Waitlist"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActivityCard;